import { EFrequency } from '@/context/borrower';

export interface ReverseHecsResult {
    grossIncome: number; // The total income before HECS is deducted
    hecsRepayment: number; // The amount that was deducted as HECS repayment
    netIncome: number; // The net income (the amount deposited in the bank)
}

/**
 * Example 2023–2024 HECS/HELP repayment thresholds and rates (approximate).
 * Note: The 2024–25 thresholds are not yet released and will likely change.
 */
const hecsRepaymentTable = [
    { min: 0, max: 54435, rate: 0.0 },
    { min: 54435, max: 62850, rate: 0.01 },
    { min: 62851, max: 66620, rate: 0.02 },
    { min: 66621, max: 70618, rate: 0.025 },
    { min: 70619, max: 74855, rate: 0.03 },
    { min: 74856, max: 79346, rate: 0.035 },
    { min: 79347, max: 84107, rate: 0.04 },
    { min: 84108, max: 89154, rate: 0.045 },
    { min: 89155, max: 94503, rate: 0.05 },
    { min: 94504, max: 100174, rate: 0.055 },
    { min: 100175, max: 106185, rate: 0.06 },
    { min: 106186, max: 112556, rate: 0.065 },
    { min: 112557, max: 119309, rate: 0.07 },
    { min: 119310, max: 126467, rate: 0.075 },
    { min: 126468, max: 134056, rate: 0.08 },
    { min: 134057, max: 142100, rate: 0.085 },
    { min: 142101, max: 150626, rate: 0.09 },
    { min: 150627, max: 159663, rate: 0.095 },
    { min: 159664, max: 1000000, rate: 0.1 },
];

/**
 * Reverse calculates the gross income and HECS/HELP repayment given the net income deposited.
 *
 * The net income is what the employee receives after HECS has been deducted.
 * Since the relationship is:
 *    netIncome = grossIncome * (1 - hecsRate)
 * and hecsRate depends on the annualized gross income,
 * we iterate over each bracket to find a candidate gross income that, when annualized,
 * falls within that bracket.
 *
 * @param netIncome    The net pay deposited (after HECS deduction) for the period.
 * @param payFrequency The pay frequency ("weekly", "fortnightly", or "monthly").
 * @returns An object containing the gross income (before HECS deduction),
 *          the HECS repayment amount, and the net income.
 */
export function calculateHecsAndNetIncome(
    payAmount: number,
    otherIncome: number,
    payFrequency: EFrequency | undefined
): {
    hecsRepayment: number; // The total income before HECS is deducted
    netIncome: number; // The net income (the amount deposited in the bank)
} {
    // 1) Sum up this period's total gross income
    const totalPeriodIncome = payAmount + otherIncome;

    // 2) Convert the pay-period income to an approximate annual income
    let annualIncome = 0;
    switch (payFrequency) {
        case EFrequency.WEEKLY:
            annualIncome = totalPeriodIncome * 52;
            break;
        case EFrequency.FORTNIGHTLY:
            annualIncome = totalPeriodIncome * 26;
            break;
        case EFrequency.MONTHLY:
            annualIncome = totalPeriodIncome * 12;
            break;
    }

    // 3) Determine the HECS/HELP repayment rate for this annualized income
    const hecsRate = getHecsRepaymentRate(annualIncome);

    // 4) Calculate the actual HECS repayment for THIS pay period
    //    (i.e., apply the same rate to the period’s income)
    const hecsRepayment = totalPeriodIncome * hecsRate;

    // 5) The net income after HECS deduction
    const netIncome = totalPeriodIncome - hecsRepayment;

    return {
        hecsRepayment,
        netIncome,
    };
}
function getHecsRepaymentRate(annualIncome: number): number {
    const bracket = hecsRepaymentTable.find(
        (b) => annualIncome >= b.min && annualIncome < b.max
    );
    return bracket ? bracket.rate : 0;
}
